/* istanbul ignore file */

import React, { MouseEvent } from 'react';
import { Hyperlink, IconHelp, Tooltip } from '@noths/polaris-client-ribbons-design-system';
import { getDeliveryByEventName, priceFormatter } from '@noths/polaris-client-utils';
import { DeliveryByEvent } from '@noths/polaris-dev-ts-types';
import * as R from 'ramda';

import { getFormattedDateString } from 'src/utils';
import { FREE_DELIVERY } from '../../DeliveryBlock';
import { DeliveryOptionInfo } from '../../DeliveryBlock/components/DeliveryBlock';
import { copy } from '../constants';
import * as styles from '../styles';
import { getReplacedPlaceholder, isLateForEvent } from '../utils';

interface Props extends DeliveryByEvent {
  deliveryOptions: DeliveryOptionInfo[];
  onTooltipClick: (concatenatedDeliveryOptionsCode: string) => void;
}

type MouseClickEvent = MouseEvent<HTMLAnchorElement | HTMLButtonElement>;
type MouseClickHandler = (e: MouseClickEvent) => void;

const getLateDeliveryLabel = R.compose(
  getReplacedPlaceholder(copy.LATE_DELIVERY_EVENT_LABEL),
  getDeliveryByEventName,
);

const TooltipContent = ({ deliveryOptions, lastDeliveryBy, eventName }: Props) => (
  <ul css={styles.deliveryOptionsList}>
    {deliveryOptions.map(({ date, exactDate, optionCode, min, max, name, price }) => (
      <li css={styles.deliveryOption} key={optionCode}>
        <p css={styles.deliveryType}>{name}</p>
        <div css={styles.dateAndPriceWrapper}>
          <div css={styles.dateAndPriceAndLateDeliveryWrapper}>
            <p css={styles.dateAndPrice}>
              {exactDate && date
                ? getFormattedDateString(date)
                : `${min} - ${max} days from dispatch`}
            </p>
            {isLateForEvent({ date, exactDate, min, max }, lastDeliveryBy) && (
              <p css={styles.lateDelivery}>{getLateDeliveryLabel(eventName)}</p>
            )}
          </div>
          <p css={styles.dateAndPrice}>
            {Number(price.amount) === 0
              ? FREE_DELIVERY
              : priceFormatter(price.currencyCode, Number(price.amount), false)}
          </p>
        </div>
      </li>
    ))}
  </ul>
);

interface HandleClickProps extends Props {
  e: MouseClickEvent;
  onClick: MouseClickHandler;
}

const handleClick = ({ e, onClick, onTooltipClick, deliveryOptions }: HandleClickProps) => {
  onClick(e);
  onTooltipClick(deliveryOptions.map((opt) => opt.optionCode).join(', '));
};

export const MoreOptionsTooltip = (props: Props) => (
  <Tooltip
    alignment="right"
    content={<TooltipContent {...props} />}
    css={styles.tooltipContentWrapper}
  >
    {({ onClick }) => (
      <Hyperlink onClick={(e) => handleClick({ e, onClick, ...props })}>
        <span css={styles.tooltipLabel}>
          {copy.DELIVERY_EVENT_MORE_OPTIONS}
          <IconHelp width={16} height={16} css={styles.tooltipIcon} />
        </span>
      </Hyperlink>
    )}
  </Tooltip>
);
